/**
 * Ledger: cash held outside the bank, and personal purchases deducted from directors' wages.
 *
 * Wage deductions: the business buys the item and reclaims the VAT, so the person repays
 * only the net amount (gross less VAT) from that month's salary.
 */
import { money } from '../accounts/arrangements.model';

export const STANDARD_VAT_RATE = 0.2;

export type LedgerPerson = {
  profileId: string;
  displayName: string;
};

export type CashSource = {
  id: string;
  name: string;
  description: string | null;
  isArchived: boolean;
  sortOrder: number;
};

export type CashEntry = {
  id: string;
  sourceId: string;
  entryDate: string;
  description: string;
  /** Positive = cash in, negative = cash out. */
  amount: number;
  transferId: string | null;
  createdAt: string;
};

export type CashEntryWithBalance = CashEntry & { balance: number };

export type WageDeduction = {
  id: string;
  profileId: string;
  /** First day of the month, YYYY-MM-01. */
  deductionMonth: string;
  purchasedOn: string;
  description: string;
  supplier: string | null;
  grossAmount: number;
  vatAmount: number;
  netAmount: number;
  /** Set when the row was created by a recurring schedule. */
  recurrenceId: string | null;
};

export type WageDeductionInput = Omit<WageDeduction, 'id' | 'netAmount' | 'recurrenceId'>;

/** A repeating deduction: every `intervalMonths` from `startMonth`, until `endMonth` if set. */
export type WageDeductionRecurrence = {
  id: string;
  profileId: string;
  description: string;
  supplier: string | null;
  grossAmount: number;
  vatAmount: number;
  intervalMonths: number;
  startMonth: string;
  endMonth: string | null;
};

export type WageDeductionRecurrenceInput = Omit<WageDeductionRecurrence, 'id'>;

type RecurrenceSchedule = Pick<WageDeductionRecurrence, 'intervalMonths' | 'startMonth' | 'endMonth'>;

export const RECURRENCE_INTERVALS: { months: number; label: string }[] = [
  { months: 1, label: 'Every month' },
  { months: 2, label: 'Every 2 months' },
  { months: 3, label: 'Every 3 months' },
  { months: 6, label: 'Every 6 months' },
  { months: 12, label: 'Every year' },
];

export type WageDeductionSignoff = {
  profileId: string;
  deductionMonth: string;
  netTotal: number;
  entryCount: number;
  personAffirmedBy: string | null;
  personAffirmedAt: string | null;
  adminAffirmedBy: string | null;
  adminAffirmedAt: string | null;
};

export type SignoffState = 'open' | 'awaiting-admin' | 'affirmed';

/** YYYY-MM-01 for a date string or Date. */
export function monthKey(value: string | Date): string {
  const iso = typeof value === 'string' ? value : toIsoDate(value);
  return `${iso.slice(0, 7)}-01`;
}

export function toIsoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

export function shiftMonth(month: string, by: number): string {
  const [y, m] = month.split('-').map(Number);
  const date = new Date(y, m - 1 + by, 1);
  return monthKey(date);
}

export function formatMonth(month: string): string {
  const [y, m] = month.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
}

/** VAT inside a VAT-inclusive gross amount. */
export function vatFromGross(gross: number, rate = STANDARD_VAT_RATE): number {
  if (!Number.isFinite(gross) || gross <= 0) return 0;
  return money(gross - gross / (1 + rate));
}

export function netFromGross(gross: number, vat: number): number {
  return money(gross - vat);
}

/** Oldest first, each with the holding's balance after it. */
export function withRunningBalance(entries: CashEntry[]): CashEntryWithBalance[] {
  const sorted = [...entries].sort((a, b) =>
    a.entryDate.localeCompare(b.entryDate) || a.createdAt.localeCompare(b.createdAt));
  let balance = 0;
  return sorted.map((entry) => {
    balance = money(balance + entry.amount);
    return { ...entry, balance };
  });
}

export function cashBalances(entries: CashEntry[]): Map<string, number> {
  const totals = new Map<string, number>();
  for (const entry of entries) {
    totals.set(entry.sourceId, money((totals.get(entry.sourceId) ?? 0) + entry.amount));
  }
  return totals;
}

/** Parses the "in" and "out" fields of an entry into one signed amount. */
export function signedAmount(cashIn: string, cashOut: string): number | null {
  const inValue = cashIn.trim() === '' ? 0 : Number(cashIn);
  const outValue = cashOut.trim() === '' ? 0 : Number(cashOut);
  if (!Number.isFinite(inValue) || !Number.isFinite(outValue) || inValue < 0 || outValue < 0) return null;
  const amount = money(inValue - outValue);
  return amount === 0 ? null : amount;
}

export type DeductionTotals = { gross: number; vat: number; net: number; count: number };

export function deductionTotals(rows: WageDeduction[]): DeductionTotals {
  return rows.reduce<DeductionTotals>((totals, row) => ({
    gross: money(totals.gross + row.grossAmount),
    vat: money(totals.vat + row.vatAmount),
    net: money(totals.net + row.netAmount),
    count: totals.count + 1,
  }), { gross: 0, vat: 0, net: 0, count: 0 });
}

export function signoffState(signoff: WageDeductionSignoff | null | undefined): SignoffState {
  if (!signoff?.personAffirmedAt) return 'open';
  return signoff.adminAffirmedAt ? 'affirmed' : 'awaiting-admin';
}

/** A month is past its end and still has nothing affirmed. */
export function signoffOverdue(month: string, signoff: WageDeductionSignoff | null | undefined, today: string): boolean {
  return signoffState(signoff) !== 'affirmed' && shiftMonth(month, 1) <= today;
}

export function validateDeduction(input: WageDeductionInput): string | null {
  if (!input.profileId) return 'Choose who the deduction is for.';
  if (!input.description.trim()) return 'Describe what was bought.';
  if (!Number.isFinite(input.grossAmount) || input.grossAmount <= 0) return 'Enter the gross amount paid.';
  if (!Number.isFinite(input.vatAmount) || input.vatAmount < 0) return 'VAT cannot be negative.';
  if (input.vatAmount > input.grossAmount) return 'VAT cannot be more than the gross amount.';
  return null;
}

function monthsBetween(from: string, to: string): number {
  const [fy, fm] = from.split('-').map(Number);
  const [ty, tm] = to.split('-').map(Number);
  return (ty - fy) * 12 + (tm - fm);
}

/** Whether a schedule has a deduction in the given month. */
export function recurrenceFallsIn(recurrence: RecurrenceSchedule, month: string): boolean {
  const key = monthKey(month);
  if (key < recurrence.startMonth) return false;
  if (recurrence.endMonth && key > recurrence.endMonth) return false;
  return monthsBetween(recurrence.startMonth, key) % recurrence.intervalMonths === 0;
}

/** How many deductions a schedule makes, or null when it has no end. */
export function recurrenceOccurrences(recurrence: RecurrenceSchedule): number | null {
  if (!recurrence.endMonth) return null;
  if (recurrence.endMonth < recurrence.startMonth) return 0;
  return Math.floor(monthsBetween(recurrence.startMonth, recurrence.endMonth) / recurrence.intervalMonths) + 1;
}

export function recurrenceLabel(recurrence: RecurrenceSchedule): string {
  const every = RECURRENCE_INTERVALS.find((option) => option.months === recurrence.intervalMonths)?.label
    ?? `Every ${recurrence.intervalMonths} months`;
  const range = recurrence.endMonth
    ? `${formatMonth(recurrence.startMonth)} to ${formatMonth(recurrence.endMonth)}`
    : `from ${formatMonth(recurrence.startMonth)}, no end`;
  return `${every}, ${range}`;
}

export function validateRecurrence(input: WageDeductionRecurrenceInput): string | null {
  const problem = validateDeduction({ ...input, deductionMonth: input.startMonth, purchasedOn: input.startMonth });
  if (problem) return problem;
  if (!RECURRENCE_INTERVALS.some((option) => option.months === input.intervalMonths)) return 'Choose how often it repeats.';
  if (input.endMonth && monthKey(input.endMonth) < monthKey(input.startMonth)) return 'The end month is before the start month.';
  return null;
}

type Row = Record<string, any>;

export function fromDbCashSource(row: Row): CashSource {
  return {
    id: row.id,
    name: row.name,
    description: row.description ?? null,
    isArchived: Boolean(row.is_archived),
    sortOrder: Number(row.sort_order ?? 100),
  };
}

export function fromDbCashEntry(row: Row): CashEntry {
  return {
    id: row.id,
    sourceId: row.source_id,
    entryDate: row.entry_date,
    description: row.description,
    amount: Number(row.amount),
    transferId: row.transfer_id ?? null,
    createdAt: row.created_at,
  };
}

export function fromDbDeduction(row: Row): WageDeduction {
  return {
    id: row.id,
    profileId: row.profile_id,
    deductionMonth: row.deduction_month,
    purchasedOn: row.purchased_on,
    description: row.description,
    supplier: row.supplier ?? null,
    grossAmount: Number(row.gross_amount),
    vatAmount: Number(row.vat_amount),
    netAmount: Number(row.net_amount),
    recurrenceId: row.recurrence_id ?? null,
  };
}

export function fromDbRecurrence(row: Row): WageDeductionRecurrence {
  return {
    id: row.id,
    profileId: row.profile_id,
    description: row.description,
    supplier: row.supplier ?? null,
    grossAmount: Number(row.gross_amount),
    vatAmount: Number(row.vat_amount),
    intervalMonths: Number(row.interval_months),
    startMonth: row.start_month,
    endMonth: row.end_month ?? null,
  };
}

export function toDbRecurrence(input: WageDeductionRecurrenceInput): Row {
  return {
    profile_id: input.profileId,
    description: input.description.trim(),
    supplier: input.supplier?.trim() || null,
    gross_amount: money(input.grossAmount),
    vat_amount: money(input.vatAmount),
    interval_months: input.intervalMonths,
    start_month: monthKey(input.startMonth),
    end_month: input.endMonth ? monthKey(input.endMonth) : null,
  };
}

export function toDbDeduction(input: WageDeductionInput): Row {
  return {
    profile_id: input.profileId,
    deduction_month: monthKey(input.deductionMonth),
    purchased_on: input.purchasedOn,
    description: input.description.trim(),
    supplier: input.supplier?.trim() || null,
    gross_amount: money(input.grossAmount),
    vat_amount: money(input.vatAmount),
  };
}

export function fromDbSignoff(row: Row): WageDeductionSignoff {
  return {
    profileId: row.profile_id,
    deductionMonth: row.deduction_month,
    netTotal: Number(row.net_total),
    entryCount: Number(row.entry_count),
    personAffirmedBy: row.person_affirmed_by ?? null,
    personAffirmedAt: row.person_affirmed_at ?? null,
    adminAffirmedBy: row.admin_affirmed_by ?? null,
    adminAffirmedAt: row.admin_affirmed_at ?? null,
  };
}
